import { 
  Shield, 
  TrendingUp, 
  CheckCircle, 
  Eye, 
  Target, 
  Users, 
  FileCheck, 
  Scale, 
  Building, 
  Cloud,
  Lock,
  type LucideIcon
} from "lucide-react";
import i18n from "@/i18n";
import type { Blueprint } from "@/components/ui/blueprint-card";
import type { TemplateMetadata } from "@/config/template-slugs";

// Database template structure from RPC (matches get_public_templates_with_counts return type)
export interface DatabaseTemplate {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  min_tier: string;
  icon: string | null;
  duration: string | null;
  complexity: string;
  cta_text: string | null;
  is_popular: boolean;
  key_features: string[];
  control_count: number;
  evidence_count: number;
  milestone_count: number;
  policy_count: number;
  document_count: number;
  automation_count: number;
}

// Icon mapping from database string to Lucide component
const ICON_MAP: Record<string, LucideIcon> = {
  'shield': Shield,
  'trending-up': TrendingUp,
  'check-circle': CheckCircle,
  'eye': Eye,
  'target': Target,
  'users': Users,
  'file-check': FileCheck,
  'scale': Scale,
  'building': Building,
  'cloud': Cloud,
  'lock': Lock,
};

// Category display names for tabs
export const CATEGORY_DISPLAY: Record<string, string> = {
  'all': 'All Templates',
  'security': 'Security Programs',
  'privacy': 'Privacy & Data',
  'financial': 'Financial Compliance',
  'governance': 'Governance',
  'healthcare': 'Healthcare',
  'legal': 'Legal & eDiscovery',
  'government': 'Government',
  'cloud': 'Cloud Security',
  'risk': 'Risk Management',
  'audit': 'Audit & Assurance',
};

const getCategoryDisplayName = (category: string) =>
  i18n.t(`templatesPage.categories.${category}`, {
    defaultValue: CATEGORY_DISPLAY[category] || category.charAt(0).toUpperCase() + category.slice(1),
  });

// Get unique categories from templates
export const getUniqueCategories = (templates: Blueprint[]): { id: string; name: string }[] => {
  const categories = new Set(templates.map(t => t.category));
  const result = [{ id: 'all', name: i18n.t('templatesPage.allTemplates', { defaultValue: CATEGORY_DISPLAY.all }) }];
  
  categories.forEach(cat => {
    result.push({
      id: cat,
      name: getCategoryDisplayName(cat),
    });
  });
  
  return result;
};

// Get icon component from string, with fallback
const getIconComponent = (iconName: string | null, category: string): LucideIcon => {
  if (iconName && ICON_MAP[iconName]) {
    return ICON_MAP[iconName];
  }
  
  const categoryIcons: Record<string, LucideIcon> = {
    'security': Shield,
    'privacy': Eye,
    'financial': TrendingUp,
    'governance': Building,
    'healthcare': CheckCircle,
    'legal': Scale,
    'government': Building,
    'cloud': Cloud,
    'risk': Target,
    'audit': FileCheck,
  };
  
  return categoryIcons[category] || Shield;
};

// Map database tier to our tier type
const mapTier = (tier: string): TemplateMetadata['minTier'] => {
  const tierMap: Record<string, TemplateMetadata['minTier']> = {
    'essential': 'essential',
    'professional': 'professional',
    'enterprise': 'enterprise',
  };
  return tierMap[tier] || 'essential';
};

// Category feature keys for i18n
const CATEGORY_FEATURE_KEYS = [
  'security', 'privacy', 'financial', 'governance', 'healthcare',
  'legal', 'government', 'cloud', 'risk', 'audit'
] as const;

// Generate default features based on category and counts
const generateDefaultFeatures = (template: DatabaseTemplate): string[] => {
  if (template.key_features && template.key_features.length > 0) {
    return template.key_features.slice(0, 6);
  }
  
  const features: string[] = [];
  if (template.control_count > 0) {
    features.push(i18n.t('templatesPage.transformer.preConfiguredControls', { count: template.control_count }));
  }
  if (template.policy_count > 0) {
    features.push(i18n.t('templatesPage.transformer.policyTemplates', { count: template.policy_count }));
  }
  if (template.milestone_count > 0) {
    features.push(i18n.t('templatesPage.transformer.implementationMilestones', { count: template.milestone_count }));
  }
  
  if (CATEGORY_FEATURE_KEYS.includes(template.category as any)) {
    const catFeatures = i18n.t(`templatesPage.transformer.features.${template.category}`, { returnObjects: true });
    if (Array.isArray(catFeatures)) {
      features.push(...catFeatures.slice(0, 3));
    }
  }
  
  return features.slice(0, 6);
};

// Generate default outcomes based on category
const generateDefaultOutcomes = (template: DatabaseTemplate): string[] => {
  const key = CATEGORY_FEATURE_KEYS.includes(template.category as any) 
    ? template.category 
    : 'default';
  const outcomes = i18n.t(`templatesPage.transformer.outcomes.${key}`, { returnObjects: true }) as string[] | string;
  return Array.isArray(outcomes) ? outcomes as string[] : [
    'Faster compliance timeline',
    'Reduced manual effort',
    'Audit-ready documentation',
    'Continuous monitoring',
  ];
};

// Generate default includes based on counts
const generateDefaultIncludes = (template: DatabaseTemplate): string[] => {
  const includes: string[] = [];
  
  if (template.control_count > 0) {
    includes.push(i18n.t('templatesPage.transformer.controls', { count: template.control_count }));
  }
  if (template.evidence_count > 0) {
    includes.push(i18n.t('templatesPage.transformer.evidenceRequirements', { count: template.evidence_count }));
  }
  if (template.policy_count > 0) {
    includes.push(i18n.t('templatesPage.transformer.policyTemplatesCount', { count: template.policy_count }));
  }
  if (template.milestone_count > 0) {
    includes.push(i18n.t('templatesPage.transformer.milestones', { count: template.milestone_count }));
  }
  
  includes.push(i18n.t('templatesPage.transformer.implementationGuide'));
  includes.push(i18n.t('templatesPage.transformer.bestPracticesDocumentation'));
  
  return includes.slice(0, 5);
};

// Determine popularity based on template characteristics
const getPopularity = (template: DatabaseTemplate): 'high' | 'medium' | 'low' => {
  if (template.is_popular) return 'high';
  
  const highPopularitySlugs = [
    'soc-2-type-ii', 'iso-27001-isms', 'gdpr', 'nist-csf',
    'third-party-risk', 'hipaa-compliance',
  ];
  
  if (highPopularitySlugs.includes(template.slug)) return 'high';
  if (template.control_count > 80) return 'high';
  
  return 'medium';
};

// Generate CTA text based on template
const generateCta = (template: DatabaseTemplate): string => {
  if (template.cta_text) return template.cta_text;
  
  return i18n.t(`templatesPage.transformer.cta.${template.category}`, {
    defaultValue: i18n.t('templatesPage.transformer.cta.default'),
  });
};

// Main transformer function
export const transformTemplateToBlueprint = (template: DatabaseTemplate): Blueprint => {
  return {
    id: template.slug,
    dbSlug: template.slug,
    name: template.name,
    category: template.category,
    description: template.description || `Complete ${template.name} implementation framework`,
    timeline: template.duration || '60-90 days',
    difficulty: template.complexity || 'Medium',
    popularity: getPopularity(template),
    templates: template.policy_count || 20,
    automations: template.automation_count || Math.floor(template.control_count / 10) || 8,
    controls: template.control_count,
    evidenceCount: template.evidence_count,
    evidenceReady: template.evidence_count > 0,
    minTier: mapTier(template.min_tier),
    features: generateDefaultFeatures(template),
    outcomes: generateDefaultOutcomes(template),
    includes: generateDefaultIncludes(template),
    icon: getIconComponent(template.icon, template.category),
    cta: generateCta(template),
  };
};
